const RESPONSE_GROUP_IDS = ['game_invite_accepted', 'game_invite_declined'];

const stableObject = (value) => Object.fromEntries(
  Object.entries(value || {}).sort(([left], [right]) => left.localeCompare(right))
);

export const getPresentationSignature = (event) => JSON.stringify({
  category: event.category,
  channels: [...(event.channels || [])].sort(),
  cadence: event.cadence,
  repeats: event.repeats,
  preferenceCategory: event.preference_category || null,
  quietHours: stableObject(event.quiet_hours),
  pushReplacesSms: Boolean(event.push_replaces_sms),
  recipient: event.recipient,
});

export const groupTransparencyEvents = (events = []) => {
  const responseEvents = RESPONSE_GROUP_IDS
    .map(id => events.find(event => event.id === id))
    .filter(Boolean);
  const canGroupResponses = responseEvents.length === RESPONSE_GROUP_IDS.length
    && new Set(responseEvents.map(getPresentationSignature)).size === 1;

  const rows = [];
  const groupedIds = canGroupResponses ? new Set(RESPONSE_GROUP_IDS) : new Set();
  events.forEach(event => {
    if (groupedIds.has(event.id)) {
      if (event.id === RESPONSE_GROUP_IDS[0]) {
        rows.push({
          ...event,
          id: 'run_invitation_responses',
          name: 'Run invitation responses',
          events: responseEvents,
        });
      }
      return;
    }
    rows.push({ ...event, events: [event] });
  });
  return rows;
};

const clockLabel = (minutes) => {
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hour24 = Math.floor(normalized / 60);
  const minute = normalized % 60;
  const suffix = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
};

const isDuringQuietHours = (minutes, quietHours) => {
  if (!quietHours) return false;
  const normalized = ((minutes % 1440) + 1440) % 1440;
  const hour = Math.floor(normalized / 60);
  return hour >= quietHours.start_hour || hour < quietHours.end_hour;
};

const timelineItem = (minutes, label, quietHours) => {
  const dayOffset = Math.floor(minutes / 1440);
  const time = clockLabel(minutes);
  return {
    time,
    displayTime: dayOffset === -1 ? `Previous day, ${time}` : time,
    dayOffset,
    label,
    duringQuietHours: isDuringQuietHours(minutes, quietHours),
  };
};

export const buildConfirmationExample = (timeline, startMinutes = 19 * 60, quietHours = null) => {
  if (!timeline) return [];
  const reminder = startMinutes - (timeline.reminder_hours_before * 60);
  const finalReminder = startMinutes - (timeline.final_reminder_hours_before * 60);
  return [
    timelineItem(reminder, 'Confirmation reminder', quietHours),
    timelineItem(finalReminder, 'Final confirmation reminder', quietHours),
    timelineItem(finalReminder + timeline.release_minutes_after_final, 'Unconfirmed spot may be released', quietHours),
    timelineItem(startMinutes, 'Training begins', quietHours),
  ];
};

export const isExampleTimeDuringQuietHours = isDuringQuietHours;

export const getQuietHoursSummary = (event) => (
  Object.entries(event.quiet_hours || {}).some(([, treatment]) => treatment === 'immediate')
    ? 'Push may send now'
    : 'Standard rules'
);

export const getExceptionalQuietHours = (event) => Object.entries(event.quiet_hours || {})
  .filter(([, treatment]) => treatment === 'immediate');

export const getPreferenceLabel = (event) => {
  if (event.preference_category) {
    return event.preference_category.replace(/\b\w/g, letter => letter.toUpperCase());
  }
  const externalChannels = (event.channels || []).filter(channel => channel !== 'in_app');
  return externalChannels.length ? 'Channel settings' : 'Always on';
};

export const getEventCadence = (event) => (event.cadence || '')
  .replace(', subject to asynchronous processing.', '.');
